import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Users, DollarSign, Link as LinkIcon, LogOut, Loader2, Home, 
  Copy, CheckCircle, Clock, TrendingUp, Wallet, AlertCircle, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const AffiliateDashboard = () => {
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  const content = {
    en: {
      title: "Affiliate Portal",
      welcome: "Welcome",
      notAffiliate: "You're not an affiliate yet",
      applyNow: "Apply to become an affiliate",
      applyBtn: "Apply Now",
      pendingApproval: "Your application is pending approval",
      suspended: "Your affiliate account is suspended",
      referralLink: "Your Referral Link",
      copyLink: "Copy Link",
      copied: "Copied!",
      totalReferrals: "Total Referrals",
      completedReferrals: "Completed",
      pendingReferrals: "Pending",
      totalEarnings: "Total Earnings",
      pendingPayout: "Available Payout",
      commissionRate: "Commission Rate",
      recentReferrals: "Recent Referrals",
      noReferrals: "No referrals yet. Share your link to start earning!",
      status: "Status",
      amount: "Amount",
      date: "Date",
      logout: "Logout",
      backToHome: "Back to Home",
      shareMessage: "Share this link with friends. When they enroll, you earn 10% commission!",
      sar: "SAR"
    },
    ar: {
      title: "بوابة الشركاء",
      welcome: "مرحباً",
      notAffiliate: "أنت لست شريكاً بعد",
      applyNow: "تقدم لتصبح شريكاً",
      applyBtn: "تقدم الآن",
      pendingApproval: "طلبك قيد المراجعة",
      suspended: "حسابك كشريك معلق",
      referralLink: "رابط الإحالة الخاص بك",
      copyLink: "نسخ الرابط",
      copied: "تم النسخ!",
      totalReferrals: "إجمالي الإحالات",
      completedReferrals: "مكتملة",
      pendingReferrals: "قيد الانتظار",
      totalEarnings: "إجمالي الأرباح",
      pendingPayout: "المتاح للسحب",
      commissionRate: "نسبة العمولة",
      recentReferrals: "الإحالات الأخيرة",
      noReferrals: "لا توجد إحالات بعد. شارك رابطك لتبدأ الكسب!",
      status: "الحالة",
      amount: "المبلغ",
      date: "التاريخ",
      logout: "تسجيل الخروج",
      backToHome: "العودة للرئيسية",
      shareMessage: "شارك هذا الرابط مع أصدقائك. عندما يسجلون، تكسب 10% عمولة!",
      sar: "ر.س"
    }
  };

  const t = content[language];
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    const fetchDashboard = async () => {
      try {
        const response = await api.get('/api/affiliate/dashboard');
        setDashboard(response.data);
        setIsAffiliate(true);
      } catch (err) {
        if (err.response?.status === 404) {
          setIsAffiliate(false);
        } else {
          setError(err.response?.data?.detail || 'Failed to load dashboard');
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboard();
    }
  }, [user, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const response = await api.post('/api/affiliate/apply');
      setDashboard(response.data);
      setIsAffiliate(true);
      toast.success(language === 'ar' ? 'تم تقديم الطلب بنجاح!' : 'Application submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${baseUrl}?ref=${dashboard?.affiliate?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(t.copied);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
            <p className="text-gray-600">{t.welcome}, {user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={toggleLanguage}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'العربية' : 'English'}</span>
            </Button>
            <Link to="/" className="text-gray-600 hover:text-teal-600 flex items-center gap-2">
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">{t.backToHome}</span>
            </Link>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t.logout}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Not an affiliate yet */}
        {!isAffiliate && (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border">
            <TrendingUp className="w-16 h-16 text-teal-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t.notAffiliate}</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">{t.applyNow}</p>
            <Button 
              onClick={handleApply}
              disabled={applying}
              className="bg-teal-600 hover:bg-teal-700 px-8 py-3 text-lg"
            >
              {applying ? <Loader2 className="w-5 h-5 animate-spin" /> : t.applyBtn}
            </Button>
          </div>
        )}

        {/* Pending Approval */}
        {isAffiliate && dashboard?.affiliate?.status === 'pending' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 text-center mb-8">
            <Clock className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-yellow-800">{t.pendingApproval}</h2>
          </div>
        )}

        {/* Active Affiliate Dashboard */}
        {isAffiliate && dashboard?.affiliate?.status === 'active' && (
          <>
            {/* Referral Link Card */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-6 text-white mb-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold mb-2">{t.referralLink}</h2>
                  <p className="text-teal-100 text-sm mb-4">{t.shareMessage}</p>
                  <div className="flex items-center gap-3 bg-white/20 rounded-lg px-4 py-3">
                    <LinkIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 truncate text-sm">
                      {baseUrl}?ref={dashboard.affiliate.referral_code}
                    </span>
                    <Button 
                      onClick={copyReferralLink}
                      size="sm"
                      className="bg-white text-teal-600 hover:bg-gray-100"
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span className="ml-2">{copied ? t.copied : t.copyLink}</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <Users className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">{dashboard.stats.total_referrals}</h3>
                <p className="text-gray-600">{t.totalReferrals}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">{dashboard.stats.completed_referrals}</h3>
                <p className="text-gray-600">{t.completedReferrals}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <DollarSign className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">{dashboard.stats.total_earnings} {t.sar}</h3>
                <p className="text-gray-600">{t.totalEarnings}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <Wallet className="w-8 h-8 text-amber-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">{dashboard.stats.pending_payout} {t.sar}</h3>
                <p className="text-gray-600">{t.pendingPayout}</p>
              </div>
            </div>

            {/* Commission Rate */}
            <div className="bg-white rounded-xl p-6 shadow-sm border mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t.commissionRate}</h3>
                  <p className="text-gray-600 text-sm">Your earnings per successful referral</p>
                </div>
                <div className="text-4xl font-bold text-teal-600">
                  {dashboard.stats.commission_rate}
                </div>
              </div>
            </div>

            {/* Recent Referrals */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.recentReferrals}</h2>
              
              {dashboard.recent_referrals?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">{t.date}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">{t.status}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">{t.amount}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {dashboard.recent_referrals.map((referral) => (
                        <tr key={referral.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(referral.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              referral.status === 'completed' 
                                ? 'bg-green-100 text-green-700'
                                : referral.status === 'paid'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {referral.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {referral.commission_amount > 0 
                              ? `${referral.commission_amount} ${t.sar}`
                              : '-'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noReferrals}</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Suspended Account */}
        {isAffiliate && dashboard?.affiliate?.status === 'suspended' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800">{t.suspended}</h2>
          </div>
        )}
      </main>
    </div>
  );
};

export default AffiliateDashboard;
